import { useState, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  FileText, Truck, Package,  MapPin, AlertCircle, User, Clock, ShoppingCart,
  Cog, Ship, Home, FileCheck, Edit, Save, X, Receipt, PackageCheck,
  Settings2
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import { formatCurrency } from "../../lib/currency-utils";
import { AssetImageUpload } from "./AssetImageUpload";
import { toast } from 'react-toastify';
import apiService from "../../services/apiService";
import { AssetShipmentDocumentsSection } from "./AssetShipmentDocumentsSection";
import {
  extractAssetShipmentDocumentsFromResponse,
  type AssetShipmentDocument,
} from "../../lib/assetShipmentDocuments";

interface AssetDetailSidebarProps {
  asset: any;
  po: any;
  isIndividualAsset?: boolean;
  customer: any;
  supplier: any;
  assetsCategories: any[];
  invoices: any[];
  updateDetails: (asset: any) => void;
  onPrimaryImageChange?: (assetId: string, imageUrl: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onViewInvoice?: (invoice: any) => void;
  onViewPO?: (poId: string) => void;
  onInsurancePolicySaved?: () => void | Promise<void>;
}

const invoiceNumberOf = (inv: Record<string, unknown> | null | undefined) => {
  if (!inv) return "";
  const value =
    inv.number ?? inv.invoice_number ?? inv.invoiceNumber ?? inv.invoice_no;
  const text = value != null ? String(value).trim() : "";
  return text && text !== "-" && text !== "—" ? text : "";
};

const assetInvoiceIds = (asset: any): string[] => {
  const ids = [
    asset?.invoice_id,
    asset?.invoiceId,
    asset?.raw?.invoice_id,
    asset?.raw?.invoiceId,
  ]
    .filter((v) => v != null && String(v).trim() !== "")
    .map((v) => String(v));
  return [...new Set(ids)];
};

const assetLineMatchIds = (asset: any): string[] => {
  const ids = [
    asset?.id,
    asset?.asset_line_id,
    asset?.assetLineId,
    asset?.raw?.id,
    asset?.raw?.asset_line_id,
  ]
    .filter((v) => v != null && String(v).trim() !== "")
    .map((v) => String(v));
  return [...new Set(ids)];
};

const findInvoiceForAsset = (asset: any, invoices: any[] | undefined) => {
  const list = Array.isArray(invoices) ? invoices : [];
  if (!list.length) return null;

  const invoiceIds = assetInvoiceIds(asset);
  if (invoiceIds.length) {
    const byId = list.find((row) => {
      const candidates = [row?.id, row?.invoice_id, row?.invoiceId]
        .filter((v) => v != null)
        .map((v) => String(v));
      return candidates.some((id) => invoiceIds.includes(id));
    });
    if (byId) return byId;
  }

  const lineIds = assetLineMatchIds(asset);
  if (lineIds.length) {
    const byLine = list.find((row) => {
      const lines = row?.assetLineIds ?? row?.asset_line_ids ?? row?.asset_ids ?? [];
      if (!Array.isArray(lines)) return false;
      return lines.some((id: unknown) => lineIds.includes(String(id)));
    });
    if (byLine) return byLine;
  }

  return list.length === 1 ? list[0] : null;
};

const resolveInvoiceLabel = (asset: any, invoices: any[] | undefined) => {
  const fromAsset = [
    asset?.invoice_number,
    asset?.invoiceNumber,
    asset?.invoice_no,
    asset?.raw?.invoice_number,
    asset?.raw?.invoiceNumber,
    asset?.raw?.number,
  ]
    .map((v) => (v != null ? String(v).trim() : ""))
    .find((v) => v && v !== "-" && v !== "—");
  if (fromAsset) return fromAsset;

  const matched = findInvoiceForAsset(asset, invoices);
  return invoiceNumberOf(matched) || "—";
};

export function AssetDetailSidebar({
  asset,
  po,
  supplier,
  customer,
  isIndividualAsset = false,
  assetsCategories,
  invoices,
  updateDetails,
  onPrimaryImageChange,
  open,
  onOpenChange,
  onViewInvoice,
  onViewPO,
}: AssetDetailSidebarProps) {
  // const [open, setOpen] = useState(false);
  console.log("asset", asset);

  const [warrantyProvider, setWarrantyProvider] = useState(asset?.warranty_provider || "");
  const [isEditingWarranty, setIsEditingWarranty] = useState(false);

  const [warrantyStartDate, setWarrantyStartDate] = useState<Date | null>(
    asset?.warranty_start_date ? new Date(asset.warranty_start_date) : null
  );
  const [warrantyEndDate, setWarrantyEndDate] = useState<Date | null>(
    asset?.warranty_end_date ? new Date(asset.warranty_end_date) : null
  );

  const [shipmentDocuments, setShipmentDocuments] = useState<AssetShipmentDocument[]>([]);
  const [loadingShipmentDocuments, setLoadingShipmentDocuments] = useState(false);

  // Asset details states
  const [specification, setSpecification] = useState(asset?.specification || "");
  const [serialNumber, setSerialNumber] = useState(asset?.serial_number || "");
  const [assetCategory, setAssetCategory] = useState(asset?.asset_category || "");
  const [makeBrand, setMakeBrand] = useState(asset?.make_brand || "");
  const [modelNo, setModelNo] = useState(asset?.model_no || "");
  const [assetType, setAssetType] = useState(asset?.asset_type || "");
  const [isPrimaryAssetImage, setIsPrimaryAssetImage] = useState<string | null>(null);
  const [isEditingDetails, setIsEditingDetails] = useState(false);


  // Update state when asset prop changes
  useEffect(() => {
    if (asset) {
      // Update warranty details (only if not editing)
      if (!isEditingWarranty) {
        setWarrantyProvider(asset.warranty_provider || "");
        setWarrantyStartDate(asset.warranty_start_date ? new Date(asset.warranty_start_date) : null);
        setWarrantyEndDate(asset.warranty_end_date ? new Date(asset.warranty_end_date) : null);
      }

      // Update asset details (only if not editing)
      if (!isEditingDetails) {
        setSpecification(asset.specification || "");
        setSerialNumber(asset.serial_number || "");
        setAssetCategory(asset.asset_category || "");
        setMakeBrand(asset.make_brand || "");
        setModelNo(asset.model_no || "");
        setAssetType(asset.asset_type || "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    asset?.id,
    asset?.itemId,
    asset?.specification,
    asset?.serial_number,
    asset?.asset_category,
    asset?.make_brand,
    asset?.model_no,
    asset?.asset_type,
    asset?.warranty_provider,
    asset?.warranty_start_date,
    asset?.warranty_end_date,
    isEditingWarranty,
    isEditingDetails
  ]);

  const loadShipmentDocuments = useCallback(async () => {
    const assetId = asset?.id;
    if (!assetId) {
      setShipmentDocuments([]);
      return;
    }
    setLoadingShipmentDocuments(true);
    try {
      const response: unknown = await apiService.get(
        `/disbursement/get-shipment-asset-documents/${assetId}`,
      );
      setShipmentDocuments(
        extractAssetShipmentDocumentsFromResponse(response, String(assetId)),
      );
    } catch (error) {
      console.error("Failed to load shipment documents:", error);
      setShipmentDocuments([]);
    } finally {
      setLoadingShipmentDocuments(false);
    }
  }, [asset?.id]);

  useEffect(() => {
    if (open && asset?.id) {
      void loadShipmentDocuments();
    }
  }, [open, asset?.id, loadShipmentDocuments]);

  const getStatusProgress = (status?: string) => {
    switch (status) {
      case "pending": return 25;
      case "in_process": return 50;
      case "shipped": return 75;
      case "delivered": return 100;
      default: return 0;
    }
  };


  const getWarrantyStatus = () => {
    if (!warrantyStartDate || !warrantyEndDate) return { status: 'Not Set', color: 'text-gray-500' };

    const now = new Date();
    const daysUntilExpiry = Math.ceil((warrantyEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (now < warrantyStartDate) return { status: 'Not Started', color: 'text-blue-500' };
    if (now > warrantyEndDate) return { status: 'Expired', color: 'text-red-500' };
    if (daysUntilExpiry <= 30) return { status: 'Expiring Soon', color: 'text-yellow-500' };
    return { status: 'Active', color: 'text-green-500' };
  };

  const handleSaveWarranty = async () => {
    // Add actual save logic here
    try {
      const response: any = await apiService.post("/purchase-order/save-warranty-assets-details",
        { warrantyStartDate, warrantyEndDate, warrantyProvider, id: asset?.id }
      );
      toast.success("Warranty information has been updated successfully.");

    } catch {
      toast.error("Failed to updated Warranty information");
    }
    const data = {
      ...asset,
      warranty_end_date: warrantyEndDate,
      warranty_provider: warrantyProvider,
      warranty_start_date: warrantyStartDate
    }
    updateDetails(data);
    setIsEditingWarranty(false);
  };

  const handleCancelWarranty = () => {

    setIsEditingWarranty(false);
  };

  const handleSaveDetails = async () => {
    try {
      const response: any = await apiService.post("/purchase-order/save-assets-details",
        { specification, serialNumber, assetCategory, makeBrand, modelNo, assetType, id: asset?.id }
      );

      const data = {
        ...asset,
        specification: specification,
        serial_number: serialNumber,
        asset_category: assetCategory,
        make_brand: makeBrand,
        model_no: modelNo,
        asset_type: assetType
      }
      updateDetails(data);
      toast.success("Asset specification and details have been updated successfully.");

    } catch {
      toast.error("Failed to updated asset details");
    }
    setIsEditingDetails(false);
  };

  const timeline = [
    { status: "pending", label: "Order Placed", completed: getStatusProgress(asset?.status) >= 25, icon: ShoppingCart },
    { status: "in_process", label: "In Process", completed: getStatusProgress(asset?.status) >= 50, icon: Settings2 },
    { status: "shipped", label: "Shipped", completed: getStatusProgress(asset?.status) >= 75, icon: Truck },
    { status: "delivered", label: "Delivered", completed: getStatusProgress(asset?.status) >= 100, icon: PackageCheck },
  ];

  const handleIsPrimaryAssetImage = (imageUrl: string) => {
    setIsPrimaryAssetImage(imageUrl);
    if (asset?.id) {
      onPrimaryImageChange?.(asset.id, imageUrl);
    }
  };

  if (!asset) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="!w-full sm:!max-w-2xl p-0 border-none shadow-2xl overflow-hidden flex flex-col bg-[#F8FAFC] [&>button.absolute]:hidden">
        <SheetHeader className="sr-only">
          <SheetTitle>Asset Details</SheetTitle>
          <SheetDescription>View and manage asset details, specifications, and documentation.</SheetDescription>
        </SheetHeader>
        
        {/* Header Section */}
        <div className="px-6 py-4 border-b border-slate-100 bg-white shadow-2xs shrink-0 space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#E0F2FE] border border-cyan-100 text-[#007A87] shadow-2xs">
                <Package className="h-5.5 w-5.5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-slate-900 tracking-tight">
                    {asset?.item || asset?.description || "ITEM 22"}
                  </h2>
                  <span className={cn(
                    "rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border",
                    asset?.status?.toLowerCase() === 'pending'
                      ? "bg-amber-50 text-amber-700 border-amber-200/80"
                      : asset?.status?.toLowerCase() === 'delivered'
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200/80"
                        : "bg-blue-50 text-blue-700 border-blue-200/80"
                  )}>
                    • {asset?.status?.toUpperCase() || 'PENDING'}
                  </span>
                </div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mt-0.5">
                  {asset?.item || "ITEM 22"} • {assetsCategories?.find((cat) => String(asset?.asset_category) === String(cat.id))?.name || "MACHINERY"}
                </p>
              </div>
            </div>

            <button
              onClick={() => onOpenChange(false)}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Quick Action Tag Pills */}
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              onClick={() => onViewPO?.(asset?.po_id)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200/80 bg-emerald-50/70 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100/60 transition-colors shadow-2xs"
            >
              <FileText className="h-3.5 w-3.5" />
              PO: {po?.purchaseOrderCode || asset?.purchase_order_code || "ANPL-PO-2608-0179"}
            </button>
            <button
              onClick={() => onViewInvoice?.(asset?.invoice_id)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200/80 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors shadow-2xs"
            >
              <Receipt className="h-3.5 w-3.5 text-slate-500" />
              INV #{resolveInvoiceLabel(asset, invoices)}
            </button>
          </div>
        </div>

        {/* Scrollable Content Body */}
        <ScrollArea className="flex-1 bg-[#F8FAFC]">
          <div className="p-6 space-y-5">

            {/* Section 1: Financial Summary Card */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xs space-y-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-orange-50 border border-orange-100 text-orange-500">
                  <Package className="h-4 w-4" />
                </div>
                <h3 className="text-sm font-semibold text-slate-800">Financial Summary</h3>
              </div>

              {/* Financial Table */}
              <div className="rounded-xl border border-slate-200/80 overflow-hidden bg-white">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80 border-b border-slate-200/80">
                      {["ITEM", "UNIT PRICE", "GST", "QTY", "TOTAL"].map((h, i) => (
                        <TableHead key={i} className={cn("text-[10px] font-semibold uppercase tracking-wider text-slate-400 h-9 px-4", i === 3 && "text-center", i === 4 && "text-right")}>
                          {h}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow className="border-b border-slate-100 hover:bg-slate-50/40 transition-colors">
                      <TableCell className="px-4 py-3 font-semibold text-slate-800 text-xs">
                        {asset?.item || asset?.description || "item 22"}
                      </TableCell>
                      <TableCell className="px-4 py-3 font-semibold text-slate-800 text-xs">
                        {asset?.unit_price ? formatCurrency(asset?.unit_price) : "N/A"}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <span className="font-semibold text-slate-800 text-xs">
                          {(asset?.cgst || 0) + (asset?.sgst || 0) + (asset?.igst || 0) + (asset?.utgst)}%
                        </span>
                        <span className="block rounded bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-600 w-fit mt-0.5">
                          {(asset?.igst || 0) > 0 ? "IGST" : "IGST"}
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-center">
                        <span className="inline-flex h-7 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white font-semibold text-xs text-slate-800 shadow-2xs">
                          { asset?.qty }
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right font-semibold text-[#039855] text-xs">
                        {(() => {
                          const unitPrice = asset?.unit_price;
                          const quantity = asset?.qty || 1;
                          const subtotal = unitPrice * quantity;
                          const totalTax = (asset?.cgst || 0) + (asset?.sgst || 0) + (asset?.igst || 0) + (asset?.utgst);
                          const totalValue = subtotal + (subtotal * totalTax) / 100;
                          return formatCurrency(totalValue);
                        })()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Parties & Addresses Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-50 text-indigo-500 shrink-0 mt-0.5">
                      <User className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">SUPPLIER</p>
                      <p className="text-xs font-semibold text-slate-800 leading-tight mt-0.5">
                        {supplier?.companyName || supplier?.company_name || 'SWIGGY LIMITED'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-50 text-pink-500 shrink-0 mt-0.5">
                      <User className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">CUSTOMER</p>
                      <p className="text-xs font-semibold text-slate-800 leading-tight mt-0.5">
                        {customer?.entityName || 'JAIKA MOTORS PRIVATE LIMITED'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-50 text-rose-500 shrink-0 mt-0.5">
                      <MapPin className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">SHIPPING ADDRESS</p>
                      <p className="text-xs font-medium text-slate-600 leading-relaxed mt-0.5">
                        {po?.supplierAddress || asset?.shipped_from || 'chennai business Building., Omnis consectetur no, GWALIOR, Bihar - 474001'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-50 text-rose-500 shrink-0 mt-0.5">
                      <MapPin className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">DELIVERY ADDRESS</p>
                      <p className="text-xs font-medium text-slate-600 leading-relaxed mt-0.5">
                        {po?.deliveryAddress || asset?.delivery_address || 'JAIKA BUILDING, COMMERCIAL ROAD, CIVIL LINES, , NAGPUR, Maharashtra - 440001'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Technical Specifications Card */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xs space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Settings2 className="h-4 w-4 text-slate-500" />
                  <h3 className="text-sm font-semibold text-slate-800">Technical Specifications</h3>
                </div>

                {!isEditingDetails ? (
                  <button
                    onClick={() => setIsEditingDetails(true)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#007A87] hover:underline"
                  >
                    <Edit className="h-3.5 w-3.5" />
                    Edit Specs
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleSaveDetails}
                      className="h-7 px-3 bg-[#007A87] text-white text-xs font-semibold hover:bg-[#006666] shadow-2xs rounded-lg"
                    >
                      <Save className="h-3 w-3 mr-1" /> Save
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditingDetails(false)}
                      className="h-7 px-2 border-slate-200 rounded-lg text-xs"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-x-6 gap-y-4 pt-1">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">SPECIFICATION</p>
                  {isEditingDetails ? (
                    <Input value={specification} onChange={(e) => setSpecification(e.target.value)} className="h-8 text-xs mt-1" />
                  ) : (
                    <p className="text-xs font-semibold text-slate-800 truncate mt-0.5">{specification || "N/A"}</p>
                  )}
                </div>

                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">SERIAL NUMBER</p>
                  {isEditingDetails ? (
                    <Input value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} className="h-8 text-xs mt-1" />
                  ) : (
                    <p className="text-xs font-semibold text-slate-800 truncate mt-0.5">{serialNumber || "N/A"}</p>
                  )}
                </div>

                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">CATEGORY</p>
                  {isEditingDetails ? (
                    <Select value={String(assetCategory)} onValueChange={setAssetCategory}>
                      <SelectTrigger className="h-8 text-xs mt-1">
                        <SelectValue placeholder="Select Category" />
                      </SelectTrigger>
                      <SelectContent>
                        {assetsCategories.map((cat) => (
                          <SelectItem key={cat.id} value={String(cat.id)}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-xs font-semibold text-slate-800 truncate mt-0.5">
                      {assetsCategories.find((cat) => String(assetCategory) === String(cat.id))?.name || "N/A"}
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">MAKE / BRAND</p>
                  {isEditingDetails ? (
                    <Input value={makeBrand} onChange={(e) => setMakeBrand(e.target.value)} className="h-8 text-xs mt-1" />
                  ) : (
                    <p className="text-xs font-semibold text-slate-800 truncate mt-0.5">{makeBrand || "N/A"}</p>
                  )}
                </div>

                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">MODEL NO</p>
                  {isEditingDetails ? (
                    <Input value={modelNo} onChange={(e) => setModelNo(e.target.value)} className="h-8 text-xs mt-1" />
                  ) : (
                    <p className="text-xs font-semibold text-slate-800 truncate mt-0.5">{modelNo || "N/A"}</p>
                  )}
                </div>

                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">ASSET CONDITION</p>
                  {isEditingDetails ? (
                    <Select value={assetType} onValueChange={setAssetType}>
                      <SelectTrigger className="h-8 text-xs mt-1">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="refurbished">Refurbished</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="inline-block mt-0.5 rounded-md bg-cyan-50 border border-cyan-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-[#007A87]">
                      {assetType || "IT-EQUIPMENT"}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Section 3: Shipment Documents Section */}
            <AssetShipmentDocumentsSection
              documents={shipmentDocuments}
              loading={loadingShipmentDocuments}
            />

            {/* Section 4: Asset Images Upload Section */}
            <AssetImageUpload assetId={asset?.id} handleIsPrimaryAssetImage={handleIsPrimaryAssetImage} />

            {/* Notes Section */}
            {asset?.notes && (
              <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xs space-y-2">
                <h3 className="text-sm font-semibold text-slate-800">Notes</h3>
                <div className="bg-slate-50 p-3 rounded-xl border-l-4 border-[#007A87]">
                  <p className="text-xs text-slate-600 whitespace-pre-wrap">{asset?.notes}</p>
                </div>
              </div>
            )}

          </div>
        </ScrollArea>

        {/* Bottom Action Footer Bar */}
        <div className="p-4 border-t border-slate-200/80 bg-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-400">
            <Clock className="h-3.5 w-3.5" />
            <span>Last updated: {asset?.last_updated ? new Date(asset.last_updated).toLocaleString() : "8/17/2026, 11:40:47 AM"}</span>
          </div>

          <Button
            onClick={() => onOpenChange(false)}
            className="h-10 rounded-xl bg-[#007A87] hover:bg-[#006666] text-xs font-semibold text-white px-5 shadow-sm flex items-center gap-1.5 transition-all"
          >
            Close <X className="h-4 w-4" />
          </Button>
        </div>

      </SheetContent>
    </Sheet>
  );
}